import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import debounce from 'lodash/debounce'

import { sendClickSoundCommand, sendHoverSoundCommand } from '../../helpers/web_to_client'
import { KEY_CODES } from '../../constants'

import styles from './InputNumber.css'

export default class InputNumber extends React.PureComponent {
    static SPEED = 50
    static DELAY = 300
    static DEBOUNCE_TIME = 1000

    static propTypes = {
        value: PropTypes.number.isRequired,
        valueMax: PropTypes.number.isRequired,

        valueMin: PropTypes.number,
        step: PropTypes.number,
        type: PropTypes.oneOf(['gold', 'regular']),
        size: PropTypes.oneOf(['default', 'medium', 'large']),
        isDisabled: PropTypes.bool,
        isFocusOn: PropTypes.bool,
        mixClassName: PropTypes.string,
        tabIndex: PropTypes.number,
        debounceTime: PropTypes.number,

        onChange: PropTypes.func.isRequired,
        tooltipId: PropTypes.string,
    }

    static defaultProps = {
        size: 'default',
        valueMin: 0,
        step: 1,
        tabIndex: -1,
        debounceTime: InputNumber.DEBOUNCE_TIME,
    }

    constructor(props) {
        super(props)

        this.state = {
            value: props.value.toString(),
            dirty: false, // do not lock controls if value is dirty
        }

        this.timer = null
        this.onChangeDebounce = debounce(this.doOnChange, props.debounceTime)
        this.flush = () => this.onChangeDebounce.flush()
    }

    inputRef = (c) => this._input = c
    
    componentDidMount() {
        this.props.isFocusOn && this.setFocusOnInput()
    }

    componentWillReceiveProps({ value }) {
        if (value !== this.props.value && value !== this.getCurrentValueAsNumber()) {
            this.setState({
                value: value.toString(),
                dirty: false,
            })
        }
    }

    componentWillUnmount() {
        this.stop()
        this.flush()
    }

    getCurrentValueAsNumber() {
        return Number(this.state.value)
    }

    getValidValue(value) {
        const minOrValue = Math.max(this.props.valueMin, Number(value))
        return Math.min(this.props.valueMax, minOrValue)
    }

    changeValue(value, dirty = false) {
        const valueString = value.toString()
        if (valueString !== this.state.value) {
            this.setState({
                value: valueString,
                dirty,
            })

            if (dirty) {
                this.props.onChange(value, false)
                this.onChangeDebounce(value)
            } else {
                this.props.onChange(value, true)
                this.onChangeDebounce.cancel()
            }
        }
    }

    doOnChange(value) {
        value = this.getValidValue(value)
        this.setState({
            value: value.toString(),
            dirty: false,
        })
        this.props.onChange(value, true)
    }

    handleChange = (event) => {
        // we replace empty string with "0" symbol
        let value = event.target.value.trim().replace(/\D/g, '') || 0
        value = Math.min(this.props.valueMax, value)
        this.changeValue(value, true)
    }

    handleBlur = () => {
        const value = this.getValidValue(this.state.value)
        if (value.toString() !== this.state.value) {
            this.changeValue(value)
        }
    }

    handleWheel = (event) => {
        if (event.cancelable) {
            event.preventDefault()
        }
        const wheelingUp = event.deltaY < 0
        if (wheelingUp) {
            this.increment()
        } else {
            this.decrement()
        }
    }

    handleMouseUp = () => {
        this.stop()
    }

    handleMouseLeave = () => {
        this.stop()
    }

    handleKeyDown = (event) => {
        if (Object.values(KEY_CODES).includes(event.keyCode)) {
            event.preventDefault()
        }
        switch (event.keyCode) {
            case KEY_CODES.ARROW_UP:
            case KEY_CODES.NUM_PLUS:
                this.increment()
                break
            case KEY_CODES.ARROW_DOWN:
            case KEY_CODES.NUM_MINUS:
                this.decrement()
                break
            case KEY_CODES.HOME:
                this.changeValue(this.props.valueMin)
                break
            case KEY_CODES.END:
                this.changeValue(this.props.valueMax)
                break
        }
    }

    increment() {
        const nextValue = Math.min(this.getValidValue(this.state.value) + this.props.step, this.props.valueMax)
        this.changeValue(nextValue)
    }

    decrement() {
        const nextValue = Math.max(this.getValidValue(this.state.value) - this.props.step, this.props.valueMin)
        this.changeValue(nextValue)
    }

    incrementHandleMouseDown = (event) => {
        const isRecursiveCall = !event
        
        this.stop()
        if (this.getCurrentValueAsNumber() < this.props.valueMax) {
            !isRecursiveCall && sendClickSoundCommand()
            this.setFocusOnInput()

            this.increment()
            this.timer = setTimeout(
                this.incrementHandleMouseDown,
                isRecursiveCall ? InputNumber.SPEED : InputNumber.DELAY
            )
        }
    }

    decrementHandleMouseDown = (event) => {
        const isRecursiveCall = !event
        
        this.stop()
        if (this.getCurrentValueAsNumber() > this.props.valueMin) {
            !isRecursiveCall && sendClickSoundCommand()
            this.setFocusOnInput()
    
            this.decrement()
            this.timer = setTimeout(
                this.decrementHandleMouseDown,
                isRecursiveCall ? InputNumber.SPEED : InputNumber.DELAY
            )
        }
    }

    stop() {
        this.timer && clearTimeout(this.timer)
        this.timer = null
    }

    setFocusOnInput = () => {
        this._input && this._input.focus()
    }

    render() {
        const { isDisabled } = this.props
        const classNameBase = classNames(styles.base, styles[`base__${this.props.size}`], {
            [styles[`base__${this.props.type}`]]: this.props.type,
            [styles.isDisabled]: isDisabled,
        }, this.props.mixClassName)

        const dirty = this.state.dirty
        const controlIncrementIsDisabled = !dirty && this.getCurrentValueAsNumber() >= this.props.valueMax || isDisabled
        const controlDecrementIsDisabled = !dirty && this.getCurrentValueAsNumber() <= this.props.valueMin || isDisabled

        const classNameControlIncrement = classNames(styles.control, styles['control__increment'], {
            [styles.isDisabled]: controlIncrementIsDisabled,
        })

        const classNameControlDecrement = classNames(styles.control, styles['control__decrement'], {
            [styles.isDisabled]: controlDecrementIsDisabled,
        })

        const dataAttrs = this.props.tooltipId ? {
            'data-tip': true,
            'data-for': this.props.tooltipId,
        } : {}

        return (
            <div className={classNameBase} tabIndex={this.props.tabIndex} {...dataAttrs}>
                <input
                    ref={this.inputRef}
                    className={styles.input}
                    type="text"
                    value={this.state.value}
                    disabled={isDisabled}
                    onChange={isDisabled ? null : this.handleChange}
                    onKeyDown={isDisabled ? null : this.handleKeyDown}
                    onWheel={isDisabled ? null : this.handleWheel}
                    onBlur={isDisabled ? null : this.handleBlur}
                />
                <div className={styles.decoration} />
                <div
                    className={classNameControlIncrement}
                    onClick={controlDecrementIsDisabled ? null : this.setFocusOnInput}
                    onMouseEnter={controlIncrementIsDisabled ? null : sendHoverSoundCommand}
                    onMouseDown={controlIncrementIsDisabled ? null : this.incrementHandleMouseDown}
                    onMouseUp={this.handleMouseUp}
                    onMouseLeave={this.handleMouseLeave}
                >
                    <span className={classNames(styles.condition, styles['condition__active'])} />
                    <span className={classNames(styles.condition, styles['condition__disabled'])} />
                </div>
                <div
                    className={classNameControlDecrement}
                    onClick={controlDecrementIsDisabled ? null : this.setFocusOnInput}
                    onMouseEnter={controlDecrementIsDisabled ? null : sendHoverSoundCommand}
                    onMouseDown={controlDecrementIsDisabled ? null : this.decrementHandleMouseDown}
                    onMouseUp={this.handleMouseUp}
                    onMouseLeave={this.handleMouseLeave}
                >
                    <span className={classNames(styles.condition, styles['condition__active'])} />
                    <span className={classNames(styles.condition, styles['condition__disabled'])} />
                </div>
            </div>
        )
    }
}



// WEBPACK FOOTER //
// ../node_modules/react-mt-bootstrap/lib/components/InputNumber/InputNumber.jsx